/**
 * @file use-server-draft.ts
 * @description Черновик формы поверх серверных данных (phase19.26 шаг 7.1).
 *
 * Раньше формы бэкапа переинициализировали черновик в `useEffect(…, [config])`,
 * и любое перечитывание конфига («Запустить сейчас», сохранение соседней формы,
 * «Обновить») стирало то, что человек уже ввёл (пароль, адрес). Здесь черновик
 * хранится вместе со снимком серверных данных, из которого он вырос:
 * - серверные данные не изменились — ничего не происходит;
 * - изменились, а черновик не трогали — черновик берёт новые данные;
 * - изменились, а черновик правили — правки остаются.
 * После своего сохранения форма вызывает `replace(ответ сервера)`.
 *
 * Отдельно помним последний пришедший проп (`seen`): после `replace(U)` проп
 * какое-то время ещё старый (страница не успела перечитать конфиг или
 * перечитывание упало) — сравнение с `seen`, а не со снимком, не даёт
 * черновику откатиться к данным до сохранения.
 */

import { useCallback, useState } from "react";

export interface ServerDraftState<T> {
  /** Последнее значение пропа с серверными данными, которое видел хук. */
  seen: T;
  /** Серверные данные, из которых вырос черновик (проп или ответ на сохранение). */
  base: T;
  draft: T;
}

/** Сравнение плоских значений формы (одинаковый порядок ключей — их строит одна функция). */
function same<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Начальное состояние: черновик = серверные данные. */
export function initialServerDraftState<T>(server: T): ServerDraftState<T> {
  return { seen: server, base: server, draft: server };
}

/**
 * Состояние после прихода серверных данных `server`. Возвращает тот же объект,
 * если менять нечего (так хук понимает, что обновлять state не нужно).
 * Проп тот же, что видели раньше, — ничего не делаем, даже если снимок
 * с тех пор заменён ответом на сохранение.
 */
export function nextServerDraftState<T>(state: ServerDraftState<T>, server: T): ServerDraftState<T> {
  if (same(state.seen, server)) return state;
  if (same(state.base, server)) return { ...state, seen: server };
  return { seen: server, base: server, draft: same(state.draft, state.base) ? server : state.draft };
}

/** Состояние после своего сохранения: черновик и снимок — из ответа сервера. */
export function replacedServerDraftState<T>(state: ServerDraftState<T>, next: T): ServerDraftState<T> {
  return { seen: state.seen, base: next, draft: next };
}

export function useServerDraft<T>(server: T) {
  const [state, setState] = useState<ServerDraftState<T>>(() => initialServerDraftState(server));

  // Подстройка состояния во время рендера (паттерн React «по изменению пропсов»):
  // без эффекта и лишнего кадра со старым черновиком.
  const current = nextServerDraftState(state, server);
  if (current !== state) setState(current);

  const setDraft = useCallback((update: (draft: T) => T) => {
    setState((s) => ({ ...s, draft: update(s.draft) }));
  }, []);
  /** «Сбросить» — вернуть черновик к серверным данным. */
  const reset = useCallback(() => setState((s) => ({ ...s, draft: s.base })), []);
  /** После своего сохранения — черновик и снимок из ответа сервера. */
  const replace = useCallback((next: T) => setState((s) => replacedServerDraftState(s, next)), []);

  return {
    draft: current.draft,
    base: current.base,
    dirty: !same(current.draft, current.base),
    setDraft,
    reset,
    replace,
  };
}
